import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type MutableRefObject,
} from "react";
import clsx from "clsx";
import { X } from "lucide-react";

import styles from "./file-field.module.css";

export type FileFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "id" | "type" | "children" | "onChange" | "value" | "defaultValue"
> & {
  id: string;
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  showErrorMessage?: boolean;
  accept?: string;
  /**
   * Uncontrolled file input: receives the first selected file, or `null` when the
   * selection is cleared (native reset or empty pick where supported).
   */
  onChange?: (file: File | null) => void;
  /**
   * File already held in parent state (e.g. after navigating back). The native input cannot
   * display this; we show the name and keep clear enabled until the user replaces or clears.
   */
  stagedFile?: File | null;
  /** When true (default), show an in-field clear control only after the user has chosen a file. */
  showClearButton?: boolean;
  /** Visible label for the clear action (also used as fallback for the icon button). */
  clearButtonLabel?: string;
};

export const FileField = forwardRef<HTMLInputElement, FileFieldProps>(
  function FileField(
    {
      id,
      label,
      required = false,
      hint,
      error,
      showErrorMessage = true,
      accept,
      disabled,
      className,
      onChange,
      stagedFile = null,
      showClearButton = true,
      clearButtonLabel = "Remove file",
      ...rest
    },
    ref
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [pickedLabel, setPickedLabel] = useState<string | null>(() => stagedFile?.name ?? null);
    const captionId = `${id}-caption`;

    useEffect(() => {
      if (stagedFile != null) {
        setPickedLabel(stagedFile.name);
        return;
      }
      const el = inputRef.current;
      if (!el?.files?.length) {
        setPickedLabel(null);
      }
    }, [stagedFile]);

    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref != null) {
          (ref as MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [ref],
    );

    const handleClear = useCallback(() => {
      if (disabled) return;
      const el = inputRef.current;
      if (el) {
        el.value = "";
      }
      setPickedLabel(null);
      onChange?.(null);
    }, [disabled, onChange]);

    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy =
      error != null && error !== ""
        ? errorId
        : hint != null && hint !== ""
          ? hintId
          : undefined;
    const hasError = error != null && error !== "";

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setPickedLabel(file?.name ?? null);
        onChange?.(file);
      },
      [onChange],
    );

    const hasSelection = pickedLabel != null && pickedLabel !== "";
    const showClear = showClearButton && !disabled && hasSelection;
    const displayText = pickedLabel ?? "No file chosen";

    return (
      <div className={styles.wrapper}>
        {label ? (
          <div id={captionId} className={clsx("sub-heading-md", styles.label)}>
            {label}
            {required ? (
              <span className="text-danger text-nowrap" aria-hidden>
                {"\u00A0*"}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className={styles.control}>
          <div
            className={clsx(
              styles.inputShell,
              hasError && styles.inputShellError,
              disabled && styles.inputShellDisabled,
              className,
            )}
          >
            <input
              {...rest}
              ref={setRefs}
              id={id}
              type="file"
              accept={accept}
              disabled={disabled}
              required={required && !hasSelection}
              className={styles.visuallyHiddenInput}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={describedBy}
              aria-labelledby={label ? captionId : undefined}
              onChange={handleChange}
            />
            <label htmlFor={id} className={clsx("body-sm", styles.pickTrigger)}>
              Choose file
            </label>
            <span
              className={clsx("body-sm", styles.fileDisplay, !hasSelection && styles.fileDisplayEmpty)}
              title={hasSelection ? (pickedLabel ?? undefined) : undefined}
              aria-live="polite"
            >
              {displayText}
            </span>
            {showClear ? (
              <button
                type="button"
                className={styles.clearButton}
                onClick={handleClear}
                title={clearButtonLabel}
                aria-label={clearButtonLabel}
              >
                <X size={18} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
          {hasError ? (
            showErrorMessage ? (
              <p id={errorId} className={clsx(styles.message, styles.messageError)} role="alert">
                {error}
              </p>
            ) : (
              <span id={errorId} className="visually-hidden" role="alert">
                {error}
              </span>
            )
          ) : hint != null && hint !== "" ? (
            <p id={hintId} className={clsx(styles.message, styles.messageHint)}>
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    );
  },
);

FileField.displayName = "FileField";
